import * as React from 'react';
import { classes } from 'typestyle';
import { encodeSvg } from '../../icons/Icons';
import { FocusVisibleIndicator } from '../../styles/FocusIndicators';
import { makeTestId } from '../../utils/Declarative';
import { isElementOverflownX } from '../../utils/DomUtil';
import { BadgeWidget } from '../Badge/BadgeWidget';
// eslint-disable-next-line import/no-cycle
import { TabContent } from './TabsWidget';
import { DefaultTabLabelStyleName, LabelStyle, TabContentStyle, TabStyleName } from './TabsWidgetStyles';

export interface TabProps {
  ariaControls: string;
  id: string;
  index: number;
  isActive: boolean;

  /**
   * Optional value indicating the amount of flex (as a percentage) for this tab.
   * This value will be defined when the chosen TabsAlignment is 'justified'.
   */
  flexPercent?: number;

  onBlur: () => void;
  onClick: (index: number) => void;
  onFocus: (index: number) => void;
  tabContent: TabContent;
  tabContainerStyles: React.CSSProperties;
  tabIndex: 0 | -1;
}

export interface TabState {
  title?: string;
}

/**
 * TabWidget is a component that renders a tab, stylized based on whether it's active, hovered, or pressed
 */
export class TabWidget extends React.PureComponent<TabProps, TabState> {
  public state: TabState = {};
  private labelRef: React.RefObject<HTMLDivElement> = React.createRef();

  private handleMouseEnter = (): void => {
    const title = (isElementOverflownX(this.labelRef.current!) && this.labelRef.current!.textContent) || undefined;
    this.setState({ title });
  }

  private handleClick = (): void => {
    this.props.onClick(this.props.index);
  }

  private handleFocus = (): void => {
    this.props.onFocus(this.props.index);
  }

  public render(): JSX.Element {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <div
        aria-controls={this.props.ariaControls}
        aria-selected={this.props.isActive}
        className={classes(TabStyleName, FocusVisibleIndicator)}
        id={this.props.id}
        onBlur={this.props.onBlur}
        onClick={this.handleClick}
        onFocus={this.handleFocus}
        onMouseEnter={this.handleMouseEnter}
        onTouchEnd={this.handleClick}
        role='tab'
        style={{ ...(!!this.props.flexPercent && { flex: `1 0 ${this.props.flexPercent}%` }) }}
        tabIndex={this.props.tabIndex}
        title={this.props.tabContent.title || this.state.title}
      >
        <div style={this.props.tabContainerStyles}>
          <div className={DefaultTabLabelStyleName}>
            {this.renderIcon()}
            {this.renderLabel()}
          </div>
        </div>
      </div>
    );
  }

  private renderIcon(): React.ReactNode {
    const tabIcon = this.props.tabContent.icon;
    if (!tabIcon) { return undefined; }

    const iconProps = {
      'data-tb-test-id': makeTestId(this.props.id, 'icon'),
      style: TabContentStyle
    };

    const element = (typeof tabIcon === 'string') ? <img alt='' src={encodeSvg(tabIcon)} /> : tabIcon;
    return <div {...iconProps}>{element}</div>;
  }

  private renderLabel(): JSX.Element {
    const { tabContent } = this.props;
    const labelProps = {
      'data-tb-test-id': makeTestId(this.props.id, 'label'),
      ref: this.labelRef,
      style: LabelStyle
    };

    return <div style={TabContentStyle}>
      <div {...labelProps}>{tabContent.label}</div>
      {tabContent.badge && <BadgeWidget kind='outline' numeric children={tabContent.badge} testId={this.props.id} />}
    </div>;
  }
}
